import { dorotheeVoice } from "@/lib/dorotheeVoice";
import { pickPhrase } from "@/lib/dorotheePhrases";
import { useState, useEffect } from "react";
import { Sparkles, Send, Volume2, Pause, Play, Square, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useSeo } from "@/lib/useSeo";
import { IMG } from "@/lib/images";
import DorotheeIA from "@/components/DorotheeIA";

const SUGGESTIONS = [
  "Pourquoi certaines personnes disent ressentir un défunt dans une pièce ?",
  "D'où vient le pendule divinatoire ?",
  "Quelle est l'origine du tarot de Marseille ?",
  "Que raconte l'histoire de la numérologie ?",
];

function moodOf(q) {
  const s = (q || "").toLowerCase();
  if (/(tarot|carte|cartomancie|tirage)/.test(s)) return "tarot";
  if (/(médium|defunt|défunt|esprit|au-del|fant)/.test(s)) return "medium";
  if (/(légende|legende|mythe|conte)/.test(s)) return "legend";
  if (/(histoire|origine|siècle|siecle|ancien|historique)/.test(s)) return "history";
  return "mystic";
}

export default function Oracle() {
  const [mode, setMode] = useState(() => (new URLSearchParams(window.location.search).get("mode") === "draw" ? "draw" : "ask"));
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [mood, setMood] = useState("mystic");
  const [voiceError, setVoiceError] = useState("");
  const [aside, setAside] = useState(null);
  useEffect(() => dorotheeVoice.subscribe(state => {
    setSpeaking(state.busy); setPaused(state.paused); setVoiceError(state.error);
  }), []);
  useEffect(() => { setAside(pickPhrase("open")); return () => dorotheeVoice.stop(); }, []);

  useSeo({
    title: "Dorothée IA — Guidance & questions",
    description: "Posez une question à Dorothée IA : recevez une guidance brève via une carte tirée pour vous, ou une réponse cultivée sur l'ésotérisme, la médiumnité et son histoire.",
    path: "/dorothee-ia",
  });

  const stopSpeak = () => dorotheeVoice.stop();
  const pauseSpeak = () => dorotheeVoice.pause();
  const resumeSpeak = () => dorotheeVoice.resume();
  const speak = (text) => dorotheeVoice.speak(text, { gesture: true });

  const submit = async () => {
    if (question.trim().length < 3) { setError("Merci de poser une question."); return; }
    stopSpeak();
    dorotheeVoice.activate();
    setAside(pickPhrase("question", question));
    setMood(moodOf(question));
    setError(""); setLoading(true); setResult(null);
    try {
      const endpoint = mode === "draw" ? "/oracle/draw" : "/oracle/ask";
      const { data } = await api.post(endpoint, { question: question.trim() });
      track("assistant_ask", { mode });
      setResult(data);
      setAside(previous => pickPhrase("after", question) || previous);
    } catch (e) {
      setError(e?.response?.data?.detail || "Dorothée IA est momentanément indisponible.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative dl-noise overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src={IMG.libraryCandle} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #050814 0%, rgba(5,8,20,0.9) 50%, #050814 100%)" }} />
      </div>

      <section className="dl-container py-16 md:py-24">
        <div className="text-center mb-14">
          <div className="dl-eyebrow mb-4">Expérience interactive</div>
          <h1 className="serif text-4xl sm:text-5xl lg:text-6xl text-[#F3E9D2]">Demandez à Dorothée IA</h1>
          <p className="mt-5 text-[#F3E9D2]/70 max-w-2xl mx-auto text-lg">
            Pose ta question à Dorothée Lancelot IA et reçois une guidance brève à travers une carte tirée
            spécialement pour toi — ou une réponse cultivée sur l'ésotérisme et son histoire.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="order-1">
            <DorotheeIA thinking={loading} mood={mood} speaking={speaking} intervention={aside} />
          </div>

          <div className="order-2">
            <div className="flex gap-2 mb-6">
              <button data-testid="ia-mode-ask" onClick={() => { stopSpeak(); setMode("ask"); setResult(null); }}
                className={`px-5 py-2.5 text-[0.7rem] tracking-widest uppercase border transition-colors ${mode === "ask" ? "bg-[#C6A76A] text-[#050814] border-[#C6A76A]" : "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/70"}`}>
                Poser une question
              </button>
              <button data-testid="ia-mode-draw" onClick={() => { stopSpeak(); setMode("draw"); setResult(null); }}
                className={`px-5 py-2.5 text-[0.7rem] tracking-widest uppercase border transition-colors ${mode === "draw" ? "bg-[#C6A76A] text-[#050814] border-[#C6A76A]" : "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/70"}`}>
                Tirer une carte
              </button>
            </div>

            <textarea
              data-testid="ia-question-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={3}
              maxLength={400}
              placeholder={mode === "draw" ? "Formulez ce qui vous habite en ce moment…" : "Posez votre question sur l'ésotérisme, la médiumnité, l'histoire…"}
              className="w-full bg-[#0B1020] border border-[rgba(198,167,106,0.3)] p-5 text-[#F3E9D2] placeholder:text-[#F3E9D2]/35 focus:outline-none focus:border-[#C6A76A] transition-colors resize-none"
            />

            {mode === "ask" && !result && (
              <div className="flex flex-wrap gap-2 mt-3">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => setQuestion(s)} className="text-xs text-[#F3E9D2]/55 border border-white/10 px-3 py-1.5 hover:border-[#C6A76A]/50 hover:text-[#D4B978] transition-colors text-left">
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-4 mt-5">
              <button data-testid="ia-submit-btn" onClick={submit} disabled={loading} className="dl-btn dl-btn-solid disabled:opacity-60">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Un instant…</> : mode === "draw" ? <><Sparkles size={16} /> Tirer une carte</> : <><Send size={15} /> Demander</>}
              </button>
            </div>

            <p className="text-xs text-[#F3E9D2]/40 mt-4">
              Les questions de santé, de maladie et de grossesse ne reçoivent pas de réponse ici.
            </p>

            {voiceError && <p role="status" className="text-[#F3E9D2] mt-4 text-sm">{voiceError}</p>}
            {error && <p data-testid="ia-error" className="text-[#e0a0a0] mt-4 text-sm">{error}</p>}

            {result && (
              <div data-testid="ia-result" className="dl-bubble mt-8 relative border border-[rgba(198,167,106,0.35)] bg-[#0B1020]/90 p-7">
                <span className="absolute -top-2 left-8 w-4 h-4 rotate-45 bg-[#0B1020] border-l border-t border-[rgba(198,167,106,0.35)]" />
                {result.card && (
                  <div className="mb-4">
                    <div className="dl-eyebrow" style={{ fontSize: "0.6rem" }}>Carte tirée</div>
                    <div className="serif text-3xl text-[#D4B978] mt-1">{result.card}</div>
                  </div>
                )}
                <p className="serif text-lg md:text-xl text-[#F3E9D2] leading-relaxed italic">{result.answer}</p>
                <div className="flex items-center justify-between mt-5">
                  <span className="dl-eyebrow" style={{ fontSize: "0.55rem" }}>Dorothée IA · Voix de synthèse</span>
                  <div className="flex items-center gap-3">
                    {!speaking && (
                      <button data-testid="ia-voice-listen" onClick={() => speak(result.answer)} className="inline-flex items-center gap-1.5 text-xs text-[#F3E9D2]/60 hover:text-[#D4B978] transition-colors"><Volume2 size={15} /> Écouter</button>
                    )}
                    {speaking && !paused && (
                      <button data-testid="ia-voice-pause" onClick={pauseSpeak} className="inline-flex items-center gap-1.5 text-xs text-[#F3E9D2]/60 hover:text-[#D4B978] transition-colors"><Pause size={15} /> Pause</button>
                    )}
                    {speaking && paused && (
                      <button data-testid="ia-voice-resume" onClick={resumeSpeak} className="inline-flex items-center gap-1.5 text-xs text-[#F3E9D2]/60 hover:text-[#D4B978] transition-colors"><Play size={15} /> Reprendre</button>
                    )}
                    {speaking && (
                      <button data-testid="ia-voice-stop" onClick={stopSpeak} className="inline-flex items-center gap-1.5 text-xs text-[#F3E9D2]/60 hover:text-[#D4B978] transition-colors"><Square size={14} /> Stop</button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <p className="text-[0.7rem] text-[#F3E9D2]/40 italic mt-6">
              Cette guidance est proposée à titre intuitif et symbolique. Elle ne remplace jamais un avis
              médical, psychologique, juridique ou financier.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
